import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Briefcase, Headset, Landmark, Check } from "lucide-react";

const ROLES = [
  {
    id: "employee",
    label: "Employee",
    icon: User,
    tagline: "Snap, request & get reimbursed in seconds",
    description:
      "Employees raise travel requests in under a minute, snap receipts on the go, and always know exactly where their money is.",
    highlights: [
      "1-tap pre-travel request wizard with per diem auto-calculation",
      "AI receipt OCR with automatic itemization & categorization",
      "Real-time advance & claim status tracker",
      "Cash advance requests deducted automatically at settlement",
    ],
    stat: ["38 sec", "Median time to file an expense"],
  },
  {
    id: "manager",
    label: "Manager",
    icon: Briefcase,
    tagline: "Budget control & frictionless one-click approvals",
    description:
      "Managers see every request against live project budgets, with out-of-policy items flagged before they ever hit the queue.",
    highlights: [
      "Departmental budget burn meter on every approval card",
      "Out-of-policy alert flags with hard-stop escalation",
      "Multi-tier approval delegation for leave & travel",
      "Mobile instant approvals via Slack / WhatsApp hooks",
    ],
    stat: ["91%", "Requests approved within 4 hours"],
  },
  {
    id: "travel-desk",
    label: "Travel Desk",
    icon: Headset,
    tagline: "Centralized itinerary hub & GDS booking execution",
    description:
      "The travel desk receives only pre-approved requests and executes bookings directly through negotiated corporate rates.",
    highlights: [
      "Corporate flight / hotel negotiated rate inventory",
      "Auto-generated itinerary PDFs with PNR & booking refs",
      "Flight modification, cancellation & refund credit-shell manager",
      "Bulk visa documentation & forex request coordinator",
    ],
    stat: ["12 min", "Average ticket turnaround time"],
  },
  {
    id: "finance",
    label: "Finance",
    icon: Landmark,
    tagline: "Automated GL-tagging & zero-fraud settlement",
    description:
      "Finance verifies bills against policy in bulk, reconciles corporate cards, and exports clean settlements straight to the ERP.",
    highlights: [
      "Auto-reconciliation with corporate card statements",
      "Full audit trail with GST / VAT tax compliance",
      "Batch direct-deposit payout export to SAP, Tally & Oracle",
      "Spend analytics dashboard with violation heat-maps",
    ],
    stat: ["$0", "Unreconciled advances after month-end"],
  },
];

export const Roles = () => {
  const [active, setActive] = useState(0);
  const role = ROLES[active];

  return (
    <section id="roles" data-testid="roles-section" className="bg-white py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] tracking-widest text-blue-600 uppercase">Built for every stakeholder</p>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl">
            Four roles. One continuous pipeline.
          </h2>
          <p className="mt-4 text-slate-500 text-base md:text-lg">
            Every persona gets a purpose-built cockpit — no shared spreadsheets, no email chains, no lost receipts.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-2">
          {ROLES.map((r, i) => (
            <button
              key={r.id}
              data-testid={`role-tab-${r.id}-button`}
              onClick={() => setActive(i)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 active:scale-95 ${
                i === active
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <r.icon className="w-4 h-4" />
              {r.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={role.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            data-testid="role-panel"
            className="mt-10 grid lg:grid-cols-[1.2fr_0.8fr] gap-8"
          >
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 lg:p-10">
              <p className="font-mono text-[11px] tracking-widest text-blue-600 uppercase">{role.label} cockpit</p>
              <h3 className="mt-3 font-heading font-bold text-2xl text-slate-900">{role.tagline}</h3>
              <p className="mt-3 text-slate-500 leading-relaxed">{role.description}</p>
              <ul className="mt-7 space-y-3.5">
                {role.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-slate-900 p-8 lg:p-10 flex flex-col justify-between noise-bg relative overflow-hidden">
              <div>
                <p className="font-mono text-[11px] tracking-widest text-slate-400 uppercase">Measured impact</p>
                <p className="mt-4 font-heading font-extrabold text-5xl text-white">{role.stat[0]}</p>
                <p className="mt-3 text-slate-400 text-sm leading-relaxed">{role.stat[1]}</p>
              </div>
              <div className="mt-10 pt-6 border-t border-white/10">
                <p className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                  Aggregated across CTEMS enterprise deployments
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
